/**
 * The transition's documents by what they are, from the Bridge Figma file's
 * "Transition Context" modal (Context Exploration, 528:8444): each recommended
 * document (TRANSIT_CONTEXT_SLOTS) with its files, or "Missing documentation"
 * and Upload; then other documentation, with Add. Opened from the workspace
 * header's Transition Context button.
 *
 * Uploading into a slot uploads the file and sets its type to the slot's, so
 * it lands there; Add uploads unsorted and categorization sorts it once read.
 * A read file's View text opens its extracted text (what drafting reads) over
 * this modal; the trash deletes a file, after a confirm in its row.
 * Replacing a file is still to come: the API has no endpoint for it.
 *
 * A second tab, User input, lists what the user wrote in (written documents,
 * typed user_input: drafts take them as the user's statements) and adds more.
 * A third, Policies, holds guidance the drafts must follow and cite, for this
 * transition or the whole org, every section or one.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Textarea } from '../ui'
import { cn } from '../../lib/utils'
import { transitApi } from '../../services/transit'
import type { Policy, TransitDocument } from '../../types/transit'
import { matchesAccept, rejectedMessage } from '../ui/FileDropzone'
import { TRANSIT_ACCEPT, TRANSIT_CONTEXT_SLOTS } from '../../constants'
import SvgIcon from './SvgIcon'

/** The slot a document is in, by its type; -1 for other documentation. */
const slotOf = (d: TransitDocument) =>
  d.document_type ? TRANSIT_CONTEXT_SLOTS.findIndex((s) => s.types.includes(d.document_type!)) : -1

const OTHER = -1

type Tab = 'documents' | 'input' | 'policies'

export default function TransitionContextModal({ transitionId, sections, onClose }: {
  transitionId: string
  /** The transition's sections, in display order: what a policy can apply to. */
  sections: Array<{ node_key: string; name: string }>
  onClose: () => void
}) {
  const [tab, setTab] = useState<Tab>('documents')
  const [docs, setDocs] = useState<TransitDocument[] | null>(null)
  const [policies, setPolicies] = useState<Policy[] | null>(null)
  const loadPolicies = useCallback(async () => {
    try {
      setPolicies(await transitApi.listPolicies(transitionId))
    } catch (e: any) {
      setError(e.message || 'Could not load the policies')
    }
  }, [transitionId])
  useEffect(() => { loadPolicies() }, [loadPolicies])
  const policiesReading = !!policies?.some((p) => p.status === 'pending' || p.status === 'extracting')
  useEffect(() => {
    if (!policiesReading) return
    const id = setInterval(loadPolicies, 1500)
    return () => clearInterval(id)
  }, [policiesReading, loadPolicies])
  const [error, setError] = useState<string | null>(null)
  // The slot uploading now (OTHER for Add), and each slot's last problem.
  const [uploading, setUploading] = useState<number | null>(null)
  const [problems, setProblems] = useState<Record<number, string>>({})
  const input = useRef<HTMLInputElement>(null)
  const target = useRef<number>(OTHER)
  // The file whose extracted text is open over the modal.
  const [viewing, setViewing] = useState<TransitDocument | null>(null)
  // Deleting: the file asking "Delete?", the one going, and why one failed.
  const [confirming, setConfirming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<{ id: string; message: string } | null>(null)
  const remove = async (d: TransitDocument) => {
    setDeleting(d.id)
    setDeleteError(null)
    try {
      await transitApi.deleteDocument(d.id)
      setConfirming(null)
      await load()
    } catch (e: any) {
      setDeleteError({ id: d.id, message: e.message || 'Could not delete this file' })
    } finally {
      setDeleting(null)
    }
  }
  const deleteProps = (d: TransitDocument) => ({
    confirming: confirming === d.id, deleting: deleting === d.id,
    error: deleteError?.id === d.id ? deleteError.message : null,
    onAskDelete: () => { setConfirming(d.id); setDeleteError(null) },
    onCancelDelete: () => setConfirming(null), onDelete: () => remove(d),
  })
  const line = (d: TransitDocument) => <FileLine key={d.id} doc={d} onView={() => setViewing(d)} {...deleteProps(d)} />
  const viewingRef = useRef(viewing)
  viewingRef.current = viewing

  const load = useCallback(async () => {
    try {
      setDocs((await transitApi.listDocuments(transitionId)).filter((d) => d.kind !== 'generated'))
    } catch (e: any) {
      setError(e.message || 'Could not load the documents')
    }
  }, [transitionId])
  useEffect(() => { load() }, [load])

  // Refresh while a document is being read or sorted, as the page's list does.
  const busy = !!docs?.some((d) => d.status === 'pending' || d.status === 'extracting' ||
    (d.status === 'ready' && d.kind === 'uploaded' && !d.document_type && Date.now() - Date.parse(d.updated_at) < 30_000))
  useEffect(() => {
    if (!busy) return
    const id = setInterval(load, 1500)
    return () => clearInterval(id)
  }, [busy, load])

  useEffect(() => {
    // Escape closes the text first, when it's open, then the modal.
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (viewingRef.current ? setViewing(null) : onClose())
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const pick = (slot: number) => {
    target.current = slot
    input.current?.click()
  }

  const upload = async (files: File[]) => {
    const slot = target.current
    if (files.length === 0) return
    const bad = files.filter((f) => !matchesAccept(f, TRANSIT_ACCEPT))
    const ok = files.filter((f) => matchesAccept(f, TRANSIT_ACCEPT))
    setProblems((all) => {
      const { [slot]: _, ...rest } = all
      return bad.length ? { ...rest, [slot]: rejectedMessage(bad) } : rest
    })
    if (ok.length === 0) return
    setUploading(slot)
    const failed: string[] = []
    for (const f of ok) {
      try {
        const doc = await transitApi.uploadDocument(transitionId, f)
        if (slot !== OTHER) await transitApi.setDocumentType(doc.id, TRANSIT_CONTEXT_SLOTS[slot].types[0])
      } catch (e: any) {
        failed.push(`${f.name}: ${e.message || 'did not upload'}`)
      }
    }
    if (failed.length) setProblems((all) => ({ ...all, [slot]: failed.join(' ') }))
    setUploading(null)
    load()
  }

  // Files on the Documents tab; what the user wrote on User input.
  const uploads = (docs ?? []).filter((d) => d.kind === 'uploaded')
  const written = (docs ?? []).filter((d) => d.kind === 'written')
  const inSlot = (slot: number) => uploads.filter((d) => slotOf(d) === slot)
  const others = inSlot(OTHER)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="transition-context-title"
      onMouseDown={(e) => e.target === e.currentTarget && !viewing && onClose()}>
      {/* A fixed height (the design's 860, less on a short screen), so switching
          tabs doesn't resize it: the title and tabs stay put and the content
          under them scrolls. Close (or Escape) closes it. */}
      <div className="flex h-[860px] max-h-full w-full max-w-[785px] flex-col gap-8 rounded-[12px] bg-card py-6 pl-[27px] pr-6">
        <div className="flex shrink-0 items-center justify-between">
          <h2 id="transition-context-title" className="text-xl font-medium text-muted-foreground">Transition Context</h2>
          <button type="button" onClick={onClose}
            className="flex h-8 min-w-16 items-center justify-center rounded-[6px] bg-secondary px-3 text-sm font-medium leading-4 text-secondary-foreground hover:bg-secondary-hover">
            Close
          </button>
        </div>

        {/* The design system's segmented Tabs. */}
        <div role="tablist" aria-label="Context" className="-mb-[3px] -mt-[19px] flex w-fit shrink-0 gap-0 rounded-[8px] p-[3px]">
          {([['documents', 'Documents', uploads.length], ['input', 'User input', written.length], ['policies', 'Policies', policies?.length ?? 0]] as const).map(([key, label, n]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
              className={cn('flex h-[30px] items-center gap-1.5 rounded-[6px] px-4 text-sm font-medium leading-none transition-colors',
                tab === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground')}>
              {label}
              {docs && <span className="tabular-nums text-tertiary">{n}</span>}
            </button>
          ))}
        </div>

        <div className="scroll-bridge -mr-4 flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto pr-4">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {docs === null && !error ? (
          <p className="flex items-center gap-2 text-sm text-tertiary"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading documents…</p>
        ) : tab === 'policies' ? (
          <Policies transitionId={transitionId} sections={sections} policies={policies} onChanged={loadPolicies} />
        ) : docs && tab === 'input' ? (
          <UserInput transitionId={transitionId} entries={written} onAdded={load}
            onView={(d) => setViewing(d)} deleteProps={deleteProps} />
        ) : docs && (
          <>
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-foreground">Document details</p>
              <div className="flex flex-col divide-y divide-input border-y border-input">
                {TRANSIT_CONTEXT_SLOTS.map((s, i) => {
                  const files = inSlot(i)
                  return (
                    <div key={s.label} className="flex items-center justify-between gap-4 py-3">
                      <div className="flex min-w-0 flex-1 flex-col gap-2">
                        <p className="text-sm font-medium text-foreground">{s.label}</p>
                        {files.length === 0
                          ? <p className="font-mono text-xs leading-4 text-destructive">Missing documentation</p>
                          : files.map(line)}
                        {problems[i] && <p role="alert" className="text-xs text-destructive">{problems[i]}</p>}
                      </div>
                      {files.length === 0 && (
                        <button type="button" onClick={() => pick(i)} disabled={uploading !== null}
                          className="flex h-8 min-w-16 shrink-0 items-center justify-center gap-1 rounded-[6px] bg-primary px-3 text-sm font-medium leading-4 tracking-[0.56px] text-primary-foreground disabled:opacity-60">
                          {uploading === i ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Uploading</>
                            : <>Upload <SvgIcon src="/transit/icon-replace.svg" width={16} height={16} /></>}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Other documentation</p>
                <button type="button" onClick={() => pick(OTHER)} disabled={uploading !== null}
                  className="flex h-8 min-w-16 items-center justify-center gap-1 rounded-[6px] border border-input px-3 text-sm font-medium leading-4 text-foreground hover:bg-secondary disabled:opacity-60">
                  {uploading === OTHER ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    : <SvgIcon src="/transit/icon-add.svg" width={16} height={16} />}
                  Add
                </button>
              </div>
              {(others.length > 0 || problems[OTHER]) && (
                <div className="flex flex-col gap-2 border-t border-input pt-3">
                  {others.map(line)}
                  {problems[OTHER] && <p role="alert" className="text-xs text-destructive">{problems[OTHER]}</p>}
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'documents' && <div className="flex flex-col gap-1.5 text-sm">
          <p className="leading-[1.2] text-foreground">
            A completed vendor questionnaire is required. The recommended documents give Overmatch the context it
            needs to understand your program: the more you provide, the faster and more accurate your results will be.
          </p>
          <p className="text-tertiary">Required</p>
          <ul className="flex list-disc flex-col gap-1 pl-6 text-base text-muted-foreground">
            {TRANSIT_CONTEXT_SLOTS.filter((s) => s.required).map((s) => <li key={s.label}>{s.label}</li>)}
          </ul>
          <p className="mt-2 text-tertiary">Recommended</p>
          <ul className="flex list-disc flex-col gap-1 pl-6 text-base text-muted-foreground">
            {TRANSIT_CONTEXT_SLOTS.filter((s) => !s.required).map((s) => <li key={s.label}>{s.label}</li>)}
          </ul>
        </div>}
        </div>


        <input ref={input} type="file" accept={TRANSIT_ACCEPT} multiple className="hidden"
          onChange={(e) => { upload(Array.from(e.target.files ?? [])); e.target.value = '' }} />
      </div>
      {viewing && <ExtractedText doc={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}

const actionClass = 'flex h-8 shrink-0 items-center justify-center gap-1 rounded-[6px] px-3 text-sm font-medium leading-4'

interface DeleteProps {
  confirming: boolean; deleting: boolean; error: string | null
  onAskDelete: () => void; onCancelDelete: () => void; onDelete: () => void
}

/**
 * The User input tab: what the user wrote in, newest last, each with its
 * text, View text and delete; then a form to add more (POST .../documents/written).
 * Written input is ready at once and typed user input, so the next drafts
 * read it as the user's statement.
 */
function UserInput({ transitionId, entries, onAdded, onView, deleteProps }: {
  transitionId: string
  entries: TransitDocument[]
  onAdded: () => Promise<void> | void
  onView: (d: TransitDocument) => void
  deleteProps: (d: TransitDocument) => DeleteProps
}) {
  const [adding, setAdding] = useState(entries.length === 0)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await transitApi.addWrittenInput(transitionId, title, text)
      setTitle('')
      setText('')
      setAdding(false)
      await onAdded()
    } catch (e: any) {
      setError(e.message || 'Could not save this')
    } finally {
      setSaving(false)
    }
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-medium text-foreground">Written by you</p>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="flex h-8 min-w-16 items-center justify-center gap-1 rounded-[6px] border border-input px-3 text-sm font-medium leading-4 text-foreground hover:bg-secondary">
            <SvgIcon src="/transit/icon-add.svg" width={16} height={16} /> Add
          </button>
        )}
      </div>
      <p className="text-sm leading-[1.4] text-muted-foreground">
        Facts the documents don&apos;t have: the sponsor, the gaining program, what you already know. Drafts treat
        these as your statements.
      </p>
      <div className="flex flex-col divide-y divide-input border-y border-input">
        {entries.length === 0 && !adding && <p className="py-3 text-sm text-tertiary">Nothing written in yet.</p>}
        {entries.map((d) => <InputEntry key={d.id} doc={d} onView={() => onView(d)} {...deleteProps(d)} />)}
        {adding && (
          <div className="flex flex-col gap-3 py-3">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" aria-label="Title"
              className="h-10 w-full rounded-[8px] border border-input bg-card px-3 text-sm text-foreground placeholder:text-tertiary focus:outline-none focus:ring-1 focus:ring-ring" />
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} aria-label="What you know" autoFocus
              placeholder="The sponsor, the gaining program, what you already know."
              className="rounded-[8px] border-input bg-card text-sm leading-[1.5]" />
            {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
            <div className="flex justify-end gap-1">
              {entries.length > 0 && (
                <button type="button" onClick={() => { setAdding(false); setError(null) }} disabled={saving}
                  className={`${actionClass} bg-secondary text-secondary-foreground hover:bg-secondary-hover disabled:opacity-60`}>
                  Cancel
                </button>
              )}
              <button type="button" onClick={save} disabled={saving || !text.trim()}
                className={`${actionClass} bg-primary text-primary-foreground disabled:opacity-60`}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The Policies tab: guidance the drafts must follow and cite (writing
 * standards, a command's rules, a legal position). New ones apply to the
 * section and scope chosen above the list; each is read like a document.
 */
function Policies({ transitionId, sections, policies, onChanged }: {
  transitionId: string
  sections: Array<{ node_key: string; name: string }>
  policies: Policy[] | null
  onChanged: () => Promise<void> | void
}) {
  const [nodeKey, setNodeKey] = useState('')
  const [orgWide, setOrgWide] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<{ id: string; message: string } | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const sectionName = (key: string | null) => (key ? sections.find((n) => n.node_key === key)?.name ?? key : 'All sections')

  const upload = async (files: File[]) => {
    const bad = files.filter((f) => !matchesAccept(f, TRANSIT_ACCEPT))
    const ok = files.filter((f) => matchesAccept(f, TRANSIT_ACCEPT))
    setError(bad.length ? rejectedMessage(bad) : null)
    if (ok.length === 0) return
    setUploading(true)
    const failed: string[] = []
    for (const f of ok) {
      try {
        await transitApi.uploadPolicy(transitionId, f, nodeKey, orgWide)
      } catch (e: any) {
        failed.push(`${f.name}: ${e.message || 'did not upload'}`)
      }
    }
    if (failed.length) setError(failed.join(' '))
    setUploading(false)
    await onChanged()
  }
  const remove = async (p: Policy) => {
    setDeleting(p.id)
    setDeleteError(null)
    try {
      await transitApi.deletePolicy(p.id)
      setConfirming(null)
      await onChanged()
    } catch (e: any) {
      setDeleteError({ id: p.id, message: e.message || 'Could not delete this policy' })
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-medium text-foreground">Policies</p>
        <button type="button" onClick={() => input.current?.click()} disabled={uploading}
          className="flex h-8 min-w-16 items-center justify-center gap-1 rounded-[6px] border border-input px-3 text-sm font-medium leading-4 text-foreground hover:bg-secondary disabled:opacity-60">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <SvgIcon src="/transit/icon-add.svg" width={16} height={16} />}
          Add
        </button>
      </div>
      <p className="text-sm leading-[1.4] text-muted-foreground">
        Guidance the drafts must follow and cite: writing standards, a command&apos;s rules, a legal position.
      </p>
      {/* What the next policy added applies to. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground">
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">New policies apply to</span>
          <select value={nodeKey} onChange={(e) => setNodeKey(e.target.value)}
            className="h-8 rounded-[6px] border border-input bg-card py-0 pl-2.5 pr-8 text-sm leading-none text-foreground focus:outline-none focus:ring-1 focus:ring-ring">
            <option value="">All sections</option>
            {sections.map((n) => <option key={n.node_key} value={n.node_key}>{n.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={orgWide} onChange={(e) => setOrgWide(e.target.checked)} className="rounded-[4px] border-input bg-card text-primary" />
          Every transition in the org
        </label>
      </div>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      <div className="flex flex-col divide-y divide-input border-y border-input">
        {policies === null && <p className="flex items-center gap-2 py-3 text-sm text-tertiary"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading policies…</p>}
        {policies?.length === 0 && <p className="py-3 text-sm text-tertiary">No policies yet.</p>}
        {policies?.map((p) => (
          <div key={p.id} className="flex items-start justify-between gap-4 py-3">
            <div className="flex min-w-0 flex-col gap-1">
              <p className="break-words font-mono text-xs leading-4 text-secondary-foreground">{p.title}</p>
              <p className="text-xs text-tertiary">{p.transition_id ? 'This transition' : 'Every transition in the org'} · {sectionName(p.node_key)}</p>
              {(p.status === 'pending' || p.status === 'extracting') && (
                <p className="flex items-center gap-1.5 text-xs text-tertiary"><Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Reading…</p>
              )}
              {p.status === 'failed' && <p className="text-xs text-destructive">Couldn&apos;t read this policy{p.error ? `: ${p.error}` : ''}</p>}
              {deleteError?.id === p.id && <p role="alert" className="text-xs text-destructive">{deleteError.message}</p>}
            </div>
            <RowActions label={p.title} confirming={confirming === p.id} deleting={deleting === p.id}
              error={null} onAskDelete={() => { setConfirming(p.id); setDeleteError(null) }}
              onCancelDelete={() => setConfirming(null)} onDelete={() => remove(p)} />
          </div>
        ))}
      </div>
      <input ref={input} type="file" accept={TRANSIT_ACCEPT} multiple className="hidden"
        onChange={(e) => { upload(Array.from(e.target.files ?? [])); e.target.value = '' }} />
    </div>
  )
}

/** One piece of user input: its title, the start of its text, View text and delete. */
function InputEntry({ doc, onView, ...del }: { doc: TransitDocument; onView: () => void } & DeleteProps) {
  const [text, setText] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    transitApi.documentText(doc.id).then((r) => live && setText(r.text)).catch(() => {})
    return () => { live = false }
  }, [doc.id])
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-sm font-medium text-foreground">{doc.title}</p>
        <p className="line-clamp-3 whitespace-pre-line text-sm leading-[1.4] text-muted-foreground">
          {text ?? <Loader2 className="inline h-3 w-3 animate-spin text-tertiary" aria-label="Loading" />}
        </p>
        {del.error && <p role="alert" className="text-xs text-destructive">{del.error}</p>}
      </div>
      <RowActions label={doc.title} onView={onView} {...del} />
    </div>
  )
}

/** View text and the trash, or the row's "Delete this file?" confirm. */
function RowActions({ label, onView, confirming, deleting, onAskDelete, onCancelDelete, onDelete }: {
  label: string; onView?: () => void
} & DeleteProps) {
  return confirming ? (
    <div className="flex shrink-0 items-center gap-1">
      <span className="mr-1 text-sm text-muted-foreground">Delete this?</span>
      <button type="button" onClick={onCancelDelete} disabled={deleting}
        className={`${actionClass} bg-secondary text-secondary-foreground hover:bg-secondary-hover disabled:opacity-60`}>
        Cancel
      </button>
      {/* The design system's Destructive button: error text on its subtle fill. */}
      <button type="button" onClick={onDelete} disabled={deleting}
        className={`${actionClass} bg-state-error-subtle text-state-error-text hover:opacity-90 disabled:opacity-60`}>
        {deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Delete
      </button>
    </div>
  ) : (
    <div className="flex shrink-0 items-center gap-1">
      {onView && (
        <button type="button" onClick={onView} aria-label={`View the text of ${label}`}
          className={`${actionClass} bg-secondary text-secondary-foreground hover:bg-secondary-hover`}>
          View text
        </button>
      )}
      <button type="button" onClick={onAskDelete} aria-label={`Delete ${label}`} title="Delete"
        className="flex size-8 shrink-0 items-center justify-center rounded-[6px] bg-secondary text-foreground hover:bg-secondary-hover">
        <SvgIcon src="/transit/icon-trash.svg" width={16} height={16} />
      </button>
    </div>
  )
}

/** A file's name, as the design sets it (mono), with View text once it's
 *  read (or where reading stands until then) and the trash. */
function FileLine({ doc, onView, ...del }: { doc: TransitDocument; onView: () => void } & DeleteProps) {
  const reading = doc.status === 'pending' || doc.status === 'extracting'
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="break-words font-mono text-xs leading-4 text-secondary-foreground">{doc.title}</p>
        {reading && <p className="flex items-center gap-1.5 text-xs text-tertiary"><Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Reading…</p>}
        {doc.status === 'failed' && <p className="text-xs text-destructive">Couldn&apos;t read this file{doc.error ? `: ${doc.error}` : ''}</p>}
        {del.error && <p role="alert" className="text-xs text-destructive">{del.error}</p>}
      </div>
      <RowActions label={doc.title} onView={doc.status === 'ready' ? onView : undefined} {...del} />
    </div>
  )
}

/** A file's extracted text, the text drafting reads, in a dialog over the modal. */
function ExtractedText({ doc, onClose }: { doc: TransitDocument; onClose: () => void }) {
  const [text, setText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    transitApi.documentText(doc.id)
      .then((r) => live && setText(r.text))
      .catch((e) => live && setError(e.message || 'Could not load the text'))
    return () => { live = false }
  }, [doc.id])
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby={`doc-text-${doc.id}`}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex max-h-full w-full max-w-[785px] flex-col gap-6 rounded-[12px] bg-card py-6 pl-[27px] pr-6">
        <div className="flex items-start justify-between gap-4">
          <h3 id={`doc-text-${doc.id}`} className="min-w-0 break-words font-mono text-sm leading-5 text-foreground">{doc.title}</h3>
          <button type="button" onClick={onClose}
            className="flex h-8 min-w-16 shrink-0 items-center justify-center rounded-[6px] bg-secondary px-3 text-sm font-medium leading-4 text-secondary-foreground hover:bg-secondary-hover">
            Close
          </button>
        </div>
        <div className="min-h-0 rounded-md bg-foreground/[0.04] dark:bg-foreground/[0.08]">
          {error ? <p className="px-4 py-3 text-sm text-destructive">{error}</p>
            : text === null ? (
              <p className="flex items-center gap-2 px-4 py-3 text-sm text-tertiary"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading text…</p>
            ) : (
              <pre className="scroll-bridge max-h-[65vh] overflow-auto whitespace-pre-wrap px-4 py-3 font-sans text-sm leading-[1.6] text-foreground">
                {text.trim() || 'No text was found in this file.'}
              </pre>
            )}
        </div>
      </div>
    </div>
  )
}
